/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { VALIDATORS_CONFIG } from '../../../../../../../../common/constants'
import { getValidator, getTirematicsActivationStatus } from '../../../../../common/operations'
import { getAssetTypes, getVehicleTypes } from '../../properties'
import { LicensePlateField } from '../license-plate-field/license-plate-field'
import { PropertyTypeField } from '../property-type-field/property-type-field'
import { SelectVehicleConnected as SelectVehicle } from '../select-vehicle/select-vehicle-connected'
import { ContractsField } from '../contracts-field/contracts-field'
import { TariffsField } from '../tariffs-field/tariffs-field'

import styles from '../../../pages.module.css'

const LICENSE_PLATE_MIN_CHARACTERS = 2

const TIREMATICS_ACTIVATION_STATUS = Object.freeze({
    ACTIVATED: 'ACTIVATED',
    ONBOARDED: 'ONBOARDED'
})

const WebfleetSubscriptionsPage = ({
    activationCode,
    activeStep,
    choosedPropertyType,
    contractNumber,
    customerNumber,
    deviceSerialNumber,
    intl,
    isGenerationRequired,
    licensePlate,
    rmaDevices,
    setChoosedPropertyType,
    setContractNumber,
    setLicensePlate,
    setPagerFunctions,
    setSelectedSubscriptionOption,
    setStep,
    setWfSubscription,
    tankSize,
    tariffs,
    wfSubscription
}) => {
    const [contractNumberState, setContractNumberState] = useState(contractNumber)
    const tariffsFiltered = tariffs.filter(tariff => tariff.number === contractNumberState)

    const getChoosedWfSubscriptionIndex = () => {
        const choosedTariff = tariffsFiltered.indexOf(tariffsFiltered.find(t => t.tariff === wfSubscription))

        return choosedTariff < 0 ? 0 : choosedTariff
    }

    const [tariff, setTariff] = useState(getChoosedWfSubscriptionIndex())
    const [licensePlateState, setLicensePlateState] = useState(licensePlate)
    const [choosedPropertyState, setChoosedPropertyState] = useState(choosedPropertyType)
    const [serverErrorMessage, setServerErrorMessage] = useState(null)
    const [errorState, setErrorState] = useState({
        errorLicensePlate: null,
        errorTankSize: null,
        errorVehicleType: null
    })
    const { errorLicensePlate, errorTankSize, errorVehicleType } = errorState

    const conf = VALIDATORS_CONFIG.step3
    const isLink340Device = getValidator(
        conf.link340.enabled,
        conf.link340.prefix.includes(deviceSerialNumber.substring(0, 2)),
        false
    )

    const isLicencePlateMandatory = tariffsFiltered?.[tariff]?.isLicensePlateMandatory

    const goNext = async () => {
        setWfSubscription(tariffsFiltered[tariff].tariff)
        setContractNumber(tariffsFiltered[tariff].number)
        setLicensePlate(licensePlateState)
        setChoosedPropertyType(choosedPropertyState)

        setStep(activeStep + 1)
    }

    const tirematicsServerValidation = async () => {
        const fleetcareContractNumber = tariffsFiltered[tariff].fleetcareContractNumber

        await getTirematicsActivationStatus(activationCode, customerNumber, licensePlateState, fleetcareContractNumber)
            .then(response => {
                const fleetcareActions = Object.freeze({
                    [TIREMATICS_ACTIVATION_STATUS.ACTIVATED]: () => {
                        setServerErrorMessage(intl.translate('ap_error_fleetcare_activated'))
                    },
                    [TIREMATICS_ACTIVATION_STATUS.ONBOARDED]: () => {
                        goNext()
                    }
                })
                const trsStatus = response?.data?.activationStatus

                const defaultAction = () => {
                    throw new Error()
                }

                const action = fleetcareActions[trsStatus] || defaultAction

                action()
            })
            .catch(() => {
                setServerErrorMessage(intl.translate('ap_error_fleetcare_general'))
            })
    }

    const tankSizeValidator = () => {
        return [tankSize.length <= 4, /^(?:[1-9]\d*)?$/.test(tankSize)].every(condition => !!condition)
    }

    const licensePlateValidator = () => {
        return getValidator(
            conf.licensePlate.enabled,
            isLicencePlateMandatory
                ? licensePlateState.length > LICENSE_PLATE_MIN_CHARACTERS
                : licensePlateState.length === 0 || licensePlateState.length > LICENSE_PLATE_MIN_CHARACTERS
        )
    }

    const propertyStateValidator = () => {
        if (isLink340Device) {
            return !!choosedPropertyState
        }
        return true
    }

    const onNext = async () => {
        if (isLicencePlateMandatory) {
            await tirematicsServerValidation()
        } else {
            await goNext()
        }
    }

    const onBack = () => {
        if (rmaDevices.length === 0) {
            setStep(activeStep - 1)
        } else {
            setSelectedSubscriptionOption(false)
        }
    }

    useEffect(() => {
        setPagerFunctions({
            onBack,
            onNext,
            validator:
                licensePlateValidator() &&
                tankSizeValidator() &&
                tariffsFiltered?.length > 0 &&
                propertyStateValidator()
        })
    }, [tankSize, licensePlateState, choosedPropertyState, tariff, contractNumberState])

    useEffect(() => {
        setTariff(getChoosedWfSubscriptionIndex())
    }, [contractNumberState])

    const getPropertyTypeSelectFieldProps = () => {
        return isLink340Device
            ? {
                  errorHandler: !choosedPropertyState,
                  hintStringCode: 'ap_step3_assettype_hint',
                  labelStringCode: 'ap_step3_assettype_label',
                  optionsArray: getAssetTypes(intl)
              }
            : {
                  errorHandler: errorVehicleType,
                  hintStringCode: 'ap_step3_vehicletype_hint',
                  labelStringCode: 'ap_step3_vehicletype_label',
                  optionsArray: getVehicleTypes(intl)
              }
    }

    const setState = (type, value) => {
        setErrorState({ ...errorState, [type]: value })
    }

    const onChoosedPropertyTypeChange = e => {
        setChoosedPropertyState(e.target.value)

        if (!(e.target.value.length === 0)) {
            setState('errorVehicleType', null)
        }
    }
    const setTankSizeError = error => {
        setState('errorTankSize', error)
    }

    const validTankSizeField = () => {
        const error = { message: intl.translate('ap_error_maxlength', 4) + ' ' + intl.translate('ap_error_digits') }
        setTankSizeError(tankSizeValidator() ? null : error)
    }

    const onLicensePlateChange = e => {
        setLicensePlateState(e.target.value.toUpperCase())
        if (e.target.value.length > LICENSE_PLATE_MIN_CHARACTERS) {
            setState('errorLicensePlate', null)
        }
    }

    const validLicensePlate = () => {
        setState(
            'errorLicensePlate',
            licensePlateValidator()
                ? null
                : { message: intl.translate('ap_error_minlength', LICENSE_PLATE_MIN_CHARACTERS + 1) }
        )
    }

    const generationValidatorCallback = () => {
        // unused
    }

    const handleContractNumber = e => {
        setContractNumberState(e.target.value)
    }

    return (
        <div className={styles['wf-page-container']}>
            <div className={styles['page-description']}>
                <p>
                    {intl.translate('ap_step3_para_basic')} {intl.translate('ap_generic_next')}
                </p>
            </div>
            <div className={styles['page-input-container']}>
                <div className={styles['input-field']}>
                    <ContractsField
                        {...{
                            intl,
                            onChange: handleContractNumber,
                            tariffs,
                            value: contractNumberState
                        }}
                    />
                </div>
                <div className={styles['input-field']}>
                    <TariffsField
                        {...{
                            disabled: !contractNumberState || tariffsFiltered?.length === 0,
                            intl,
                            onChange: e => setTariff(e.target.value),
                            tariffs: tariffsFiltered,
                            value: tariff
                        }}
                    />
                </div>
                <div className={styles['input-field']}>
                    <PropertyTypeField
                        {...{
                            intl,
                            onChange: e => onChoosedPropertyTypeChange(e),
                            value: choosedPropertyState,
                            ...getPropertyTypeSelectFieldProps()
                        }}
                    />
                </div>
                <div className={styles['input-field']}>
                    <LicensePlateField
                        {...{
                            error: errorLicensePlate,
                            intl,
                            onChange: e => onLicensePlateChange(e),
                            onFocus: () => setState('errorLicensePlate', false),
                            validator: validLicensePlate,
                            value: licensePlateState
                        }}
                    />
                </div>
            </div>
            {isGenerationRequired ? (
                <SelectVehicle
                    {...{
                        errorTankSize: errorTankSize,
                        intl,
                        setTankSizeError: setTankSizeError,
                        validTankSizeField,
                        validatorCallback: generationValidatorCallback
                    }}
                />
            ) : (
                <span />
            )}
            <div className={styles['error-message']}>{serverErrorMessage}</div>
        </div>
    )
}

WebfleetSubscriptionsPage.propTypes = {
    activationCode: PropTypes.string,
    activeStep: PropTypes.number,
    choosedPropertyType: PropTypes.string,
    contractNumber: PropTypes.string,
    customerNumber: PropTypes.string,
    deviceSerialNumber: PropTypes.string,
    intl: PropTypes.objectOf(PropTypes.func),
    isGenerationRequired: PropTypes.boolean,
    licensePlate: PropTypes.string,
    rmaDevices: PropTypes.array,
    setChoosedPropertyType: PropTypes.func,
    setContractNumber: PropTypes.func,
    setLicensePlate: PropTypes.func,
    setPagerFunctions: PropTypes.func,
    setSelectedSubscriptionOption: PropTypes.func,
    setStep: PropTypes.func,
    setWfSubscription: PropTypes.func,
    tankSize: PropTypes.string,
    tariffs: PropTypes.array,
    wfSubscription: PropTypes.string
}

export { WebfleetSubscriptionsPage }
